'use client';

import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { type VideofeedItemPublicScope } from '@common/clients/api';
import { MediaQuery, MediaQueryDirection, useResponsive } from '@common/hooks/useResponsive';
import { useContextData } from '@common/useContextData';
import { ClickTracker } from '@web/atoms/ClickTracker';
import { TrackedItem } from '@web/atoms/TrackingItem';
import { useHasScrolled } from '@web/hooks/useHasScrolled';
import styles from '@web/organisms/VideoCarousel/molecules/Carousel/Carousel.module.scss';
import { CLICK_TRACKING_CONFIGS } from '@web/utils/gtm-tracker/config';
import { ComponentClickTargets } from '@web/utils/gtm-tracker/types/click/base';

import { Arrow, type Direction } from '../../atoms/Arrow';
import { VideoPreview } from '../VideoPreview';

const CLICK_TRACKING_CONFIG = CLICK_TRACKING_CONFIGS[ComponentClickTargets.VIDEO_CAROUSEL];

export interface Props {
    videos: VideofeedItemPublicScope[];
    activeVideoID?: string;
    onItemClick?: (videoID: string) => void;
}

export const Component = ({ videos, activeVideoID, onItemClick }: Props) => {
    const contextData = useContextData();
    const ref = useRef<HTMLUListElement | null>(null);
    const hideDuration = useMemo(() => (videos || []).some((video) => !video.duration), [videos]);

    const isResponsiveMode = useResponsive({ direction: MediaQueryDirection.below, size: MediaQuery.m });

    const { hasScrolled, hasScrolledToEnd, handleScroll } = useHasScrolled({
        scrollThreshold: 0.2,
        endScrollTolerance: 5,
        ref,
    });

    const onScroll = useCallback((direction: Direction) => {
        if (!ref.current) return;
        const scrollDistance = ref.current.clientWidth * 0.8;
        ref.current.scrollBy(direction === 'left' ? -scrollDistance : scrollDistance, 0);
    }, []);

    useEffect(() => {
        if (!ref.current) return;
        const currentIndex = videos.findIndex((video) => video.id.toString() === activeVideoID);
        const scrollLeft =
            (ref.current.scrollWidth / videos.length) * (currentIndex + (isResponsiveMode ? 1 : 0));
        ref.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }, [videos, activeVideoID, isResponsiveMode]);

    let nextIsNextVideo = false;
    return (
        <section className={styles.Carousel}>
            <ul
                style={{ [`--cmp-carousel-count` as string]: videos.length }}
                onScroll={handleScroll}
                ref={ref}
            >
                {videos.map((video) => {
                    const isNextVideo = nextIsNextVideo;
                    const isActiveVideo = video.id.toString() === activeVideoID;
                    nextIsNextVideo = isActiveVideo;
                    return (
                        <VideoPreview
                            key={video.id}
                            video={video}
                            hideDuration={hideDuration}
                            isActive={isActiveVideo}
                            isNext={isNextVideo}
                            onClick={() => {
                                if (onItemClick) {
                                    onItemClick(video.id.toString());
                                }
                            }}
                            data-track-id="carousel-item"
                            contextData={contextData}
                        />
                    );
                })}
                {isResponsiveMode ? null : (
                    <>
                        <TrackedItem trackingConfig={CLICK_TRACKING_CONFIG.CAROUSEL_ARROW_PREV}>
                            <Arrow direction="left" isHidden={!hasScrolled} onDirectionClick={onScroll} />
                        </TrackedItem>
                        <TrackedItem trackingConfig={CLICK_TRACKING_CONFIG.CAROUSEL_ARROW_NEXT}>
                            <Arrow
                                direction="right"
                                isHidden={hasScrolledToEnd}
                                onDirectionClick={onScroll}
                            />
                        </TrackedItem>
                    </>
                )}
            </ul>
            <ClickTracker targetRef={ref} {...CLICK_TRACKING_CONFIG.VIDEO_ITEM} />
        </section>
    );
};

export const Carousel = memo(Component);
